import { TextAlignment } from "building-types/annotation/annotationTypesEnum";
import type { SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import {
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
  type SheetGeometry,
} from "building-types/views/sheetGeometryTypes";
import { degreeToRadian } from "common/utils/math";
import { textAlignmentToXAnchor } from "common/utils/textAlignment";
import { mat3Dot, mat3Translate } from "geom/mat3";
import { polyLine2Transform } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2AngleBetweenCW, vec2Normalize } from "geom/vec2";
import { generateArrowheadGeometry } from "lib/house/annotation/arrowheadGeometry";

interface ElevNoteWithLongLeaderProps {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly targetRelativeX: number;
  readonly targetRelativeY: number;
  readonly alignment: TextAlignment;
}

const defaultElevNoteWithLongLeader: ElevNoteWithLongLeaderProps = {
  text: "TEST",
  x: 0,
  y: 0,
  alignment: TextAlignment.RIGHT,
  targetRelativeX: 0,
  targetRelativeY: 0,
};

export function generateElevNoteWithLongLeaderLabel(props: Partial<ElevNoteWithLongLeaderProps>): SheetTextGeometry {
  const resolvedProps = { ...defaultElevNoteWithLongLeader, ...props };
  const { text } = resolvedProps;
  const textLines = text.split("**").join("\n");
  const transform = mat3Translate(resolvedProps.x, resolvedProps.y);
  const [textX, textY] = mat3Dot(transform, -0.04, 0.04);
  // Deliberately unstyled: the MATERIAL_TAGS stylesheet owns font/size/color so View Template
  // overrides apply. Callers resolve the style before measuring or emitting.
  return sheetTextGeometryNew({
    x: textX,
    y: textY,
    text: textLines,
    alignment: resolvedProps.alignment,
    placementXAnchor: textAlignmentToXAnchor(resolvedProps.alignment),
  });
}

export function generateElevNoteWithLongLeaderSheetGeometry(
  props: Partial<ElevNoteWithLongLeaderProps>,
): SheetGeometry[] {
  const resolvedProps = { ...defaultElevNoteWithLongLeader, ...props };

  const { targetRelativeX, targetRelativeY } = resolvedProps;

  const finalTargetX = -targetRelativeX;
  const finalTargetY = -targetRelativeY;

  const deltaY = finalTargetY;
  const deltaX = deltaY / degreeToRadian(30);
  const midPointX = deltaX;

  const [vx, vy] = vec2Normalize(-(finalTargetX - midPointX), finalTargetY);
  const rotationRads = vec2AngleBetweenCW(0, 1, vx, vy);

  const transform = mat3Translate(resolvedProps.x, resolvedProps.y);

  // Generate arrowhead at the target position
  const arrowheadGeometry = generateArrowheadGeometry({
    position: [resolvedProps.x + finalTargetX, resolvedProps.y + finalTargetY],
    rotationRads,
    width: 0.06,
    height: 0.07,
  });

  const leaderPolyline = polyLine2Transform(polyLine2New([0, 0, midPointX, 0, finalTargetX, finalTargetY]), transform);

  return [
    arrowheadGeometry,
    sheetPolylineGeometryNew({ paths: [leaderPolyline] }),
    generateElevNoteWithLongLeaderLabel(resolvedProps),
  ];
}
